// @ts-nocheck — ported 1:1 from React Native provisioning-cli; runtime-validated
/**
 * Zyxel USG FLEX / ATP CLI Generator
 * ZLD OS Configuration Script Generator
 * 
 * This module generates CLI configuration scripts for Zyxel firewalls.
 * Includes Mini-ISDB (FQDN simulation), UTM profiles, and secure policies.
 */

import { LACPAggregate, WebFilterConfig, OutboundServicesConfig, WANConfig, CloudServicesMappingConfig, ManagementNetworkConfig, SmartPoliciesConfig, AppReachabilityConfig, APP_DEFINITIONS, getAppDefinition } from '../state/wizardStore';
import { IOT_FQDN_GROUPS, ALL_IOT_FQDNS, IOT_TCP_PORT_RANGES, IOT_UDP_PORT_RANGES, getIoTVlans } from '../utils/iotCloudConfig';
import { getModelByName, getVendorForModel, getLanPorts, HardwareModel, VendorDefinition } from '../hardware/hardwareDictionary';

// ============================================
// SMART POLICIES CONSTANTS
// ============================================

// VLAN Keyword Matching for Smart Policy Resolution
// Updated to match the new Opinionated UI Preset names (case-insensitive)
const VLAN_KEYWORDS = {
  // 'management' preset: Network device management
  admin: ['management', 'admin', 'amministrazione', 'mgmt'],
  
  // 'staff' preset: Employee workstations
  staff: ['staff', 'client', 'dipendenti', 'ufficio', 'office', 'corporate'],
  
  // 'guest' preset: Visitor internet access
  guest: ['guest', 'ospiti', 'visitatori', 'hotspot'],
  
  // 'printers' preset: Network printers (note: preset is 'printers' plural)
  printer: ['printers', 'printer', 'print', 'stampanti', 'stampa', 'mfp'],
  
  // 'voip' preset: IP phones
  voip: ['voip', 'telefoni', 'phone', 'sip', 'pbx'],
  
  // 'iot' preset: Smart devices
  iot: ['iot', 'domotica', 'smart', 'automation', 'cameras'],
  
  // 'ot' preset: HVAC, BMS, SCADA
  ot: ['ot', 'hvac', 'bms', 'scada', 'industrial', 'plc', 'factory'],
  
  // 'servers' preset: On-premises servers
  servers: ['servers', 'server', 'datacenter', 'vm'],
  
  // 'dmz' preset: Public-facing services
  dmz: ['dmz', 'web', 'public'],
  
  // 'blackhole' preset: Quarantine
  blackhole: ['blackhole', 'quarantine', 'blocked'],
};

// ============================================
// SECURITY-FIRST: Interface Access Control Tiers
// ============================================
type VLANSecurityTierZyxel = 'management' | 'trusted' | 'untrusted';

/**
 * Determines the security tier for a VLAN based on its type/usage
 */
function getVlanSecurityTierZyxel(vlanType?: string, vlanName?: string): VLANSecurityTierZyxel {
  const nameLower = (vlanName || '').toLowerCase();
  const typeLower = (vlanType || '').toLowerCase();
  
  // TIER 1 - Management: Full access
  if (typeLower === 'management' || 
      nameLower.includes('management') || 
      nameLower.includes('mgmt') ||
      nameLower.includes('admin')) {
    return 'management';
  }
  
  // TIER 2 - Trusted: Limited access (ping only)
  if (typeLower === 'corporate' || 
      typeLower === 'server' ||
      nameLower.includes('staff') ||
      nameLower.includes('server') ||
      nameLower.includes('voip') ||
      nameLower.includes('dmz')) {
    return 'trusted';
  }
  
  // TIER 3 - Untrusted: NO ACCESS
  return 'untrusted';
}

/**
 * Returns the Zyxel CLI management access string based on security tier
 */
function getZyxelManagementAccess(tier: VLANSecurityTierZyxel, isNativeManagement: boolean = false): string {
  if (isNativeManagement) {
    return '  management https ssh http telnet ping\n';
  }
  
  switch (tier) {
    case 'management':
      return '  management https ssh ping\n';
    case 'trusted':
      return '  management ping\n';
    case 'untrusted':
      // NO ACCESS - omit the line
      return '';
  }
}

/**
 * Find VLANs matching specific keywords in their names
 */
function findVlansByKeywordsZyxel(vlans: VLANInput[], keywords: string[]): VLANInput[] {
  return vlans.filter(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

/**
 * Find first VLAN matching specific keywords
 */
function findVlanByKeywordsZyxel(vlans: VLANInput[], keywords: string[]): VLANInput | undefined {
  return vlans.find(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

// ============================================
// MINI-ISDB: FQDN Dictionary for Cloud Services
// ============================================
interface FQDNService {
  name: string;
  displayName: string;
  fqdns: string[];
  icon: string;
}

export const CLOUD_SERVICES_FQDN: Record<string, FQDNService> = {
  whatsapp: {
    name: 'WHATSAPP',
    displayName: 'WhatsApp',
    fqdns: ['*.whatsapp.com', '*.whatsapp.net', '*.wa.me'],
    icon: 'whatsapp',
  },
  facebook: {
    name: 'FACEBOOK',
    displayName: 'Facebook',
    fqdns: ['*.facebook.com', '*.fbcdn.net', '*.facebook.net'],
    icon: 'facebook',
  },
  instagram: {
    name: 'INSTAGRAM',
    displayName: 'Instagram',
    fqdns: ['*.instagram.com', '*.cdninstagram.com'],
    icon: 'instagram',
  },
  netflix: {
    name: 'NETFLIX',
    displayName: 'Netflix',
    fqdns: ['*.netflix.com', '*.nflximg.net', '*.nflxvideo.net', '*.nflxso.net'],
    icon: 'netflix',
  },
  tiktok: {
    name: 'TIKTOK',
    displayName: 'TikTok',
    fqdns: ['*.tiktok.com', '*.tiktokcdn.com', '*.tiktokv.com'],
    icon: 'music-note',
  },
  youtube: {
    name: 'YOUTUBE',
    displayName: 'YouTube',
    fqdns: ['*.youtube.com', '*.googlevideo.com', '*.ytimg.com'],
    icon: 'youtube',
  },
  spotify: {
    name: 'SPOTIFY',
    displayName: 'Spotify',
    fqdns: ['*.spotify.com', '*.scdn.co'],
    icon: 'spotify',
  },
  microsoft365: {
    name: 'M365',
    displayName: 'Microsoft 365',
    fqdns: ['*.microsoft.com', '*.office.com', '*.office365.com', '*.teams.microsoft.com'],
    icon: 'microsoft',
  },
};

// ============================================
// INTERFACES
// ============================================
interface VLANInput {
  nome_vlan: string;
  vlan_id: string;
  network_ip: string;
  gateway_ip: string;
  subnet_mask: string;
  enable_dhcp: boolean;
  dhcp_start?: string;
  dhcp_end?: string;
  dns_primary?: string;
  dns_secondary?: string;
  porte_assegnate: string[];
  vlan_type?: 'corporate' | 'guest' | 'server' | 'management' | 'iot';
}

interface ZyxelGeneratorInput {
  hostname: string;
  timezone_id: string;
  password_admin: string;
  dns_primary: string;
  dns_secondary: string;
  modello: string;
  mgmt_port?: string;
  mgmt_ip?: string;
  mgmt_subnet?: string;
  lacp_aggregates?: LACPAggregate[];
  managementNetwork?: ManagementNetworkConfig;  // Native Management Network
  policies: string[];
  policy_config: {
    ad_source_vlan?: string;
    ad_dest_vlan?: string;
    guest_vlan?: string;
    corporate_vlans?: string[];
  };
  webfilter_config?: WebFilterConfig;
  outbound_services?: OutboundServicesConfig;
  cloud_services_mapping?: CloudServicesMappingConfig;  // Service -> VLAN IDs mapping
  smart_policies?: SmartPoliciesConfig;  // Smart Policy Builder Config
  app_reachability?: AppReachabilityConfig;  // App Reachability (L7/FQDN)
  vlans: VLANInput[];
  wan1?: WANConfig;
  wan2?: WANConfig;
}

// Zyxel Timezone Mapping
const ZYXEL_TIMEZONE_MAP: Record<string, string> = {
  '28': 'GMT+1', '0': 'GMT', '29': 'GMT+2', '27': 'GMT+0',
  '85': 'GMT-5', '86': 'GMT-6', '87': 'GMT-7', '88': 'GMT-8',
};

// ============================================
// UTILITY FUNCTIONS
// ============================================
function ipToNumber(ip: string): number {
  const parts = ip.split('.').map(Number);
  return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
}

function calculatePoolSize(startIp: string, endIp: string): number {
  return Math.max(1, ipToNumber(endIp) - ipToNumber(startIp) + 1);
}

function sanitizeObjectName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_]/g, '_').toUpperCase();
}

function getZyxelZone(vlanType?: string): string {
  switch (vlanType) {
    case 'guest': return 'LAN2';
    case 'management': return 'SYSTEM';
    default: return 'LAN1';
  }
}

/**
 * Truncate interface name to comply with Zyxel uOS 11-character limit
 * Uses intelligent abbreviation to preserve meaningful parts
 */
function truncateInterfaceName(name: string, maxLength: number = 11): string {
  if (name.length <= maxLength) return name;
  
  // Common abbreviations for VLAN names
  const abbreviations: Record<string, string> = {
    'management': 'MGMT',
    'amministrazione': 'AMMIN',
    'corporate': 'CORP',
    'dipendenti': 'DIP',
    'stampanti': 'PRINT',
    'printers': 'PRINT',
    'printer': 'PRT',
    'guest': 'GUEST',
    'ospiti': 'OSP',
    'voip': 'VOIP',
    'telefoni': 'TEL',
    'server': 'SRV',
    'servers': 'SRV',
    'iot': 'IOT',
    'domotica': 'DOM',
    'dmz': 'DMZ',
    'quarantine': 'QUAR',
    'blackhole': 'BH',
  };
  
  // Try to apply abbreviations first
  let abbreviated = name.toLowerCase();
  for (const [full, abbr] of Object.entries(abbreviations)) {
    if (abbreviated.includes(full)) {
      abbreviated = abbreviated.replace(full, abbr);
    }
  }
  
  // If still too long, truncate
  if (abbreviated.length > maxLength) {
    return abbreviated.substring(0, maxLength).toUpperCase();
  }
  
  return abbreviated.toUpperCase();
}

// ============================================
// MAIN GENERATOR FUNCTION
// ============================================
export const generateZyxelCLIScript = (input: ZyxelGeneratorInput): string => {
  let script = '';
  const lagNames: string[] = [];
  if (input.lacp_aggregates) {
    input.lacp_aggregates.forEach((_, idx) => lagNames.push(`lag${idx + 1}`));
  }

  // ============================================
  // HEADER
  // ============================================
  script += '! ============================================\n';
  script += '! Zyxel USG FLEX / ATP Configuration Script\n';
  script += `! Model: ${input.modello}\n`;
  script += `! Hostname: ${input.hostname}\n`;
  script += `! Generated: ${new Date().toISOString()}\n`;
  script += '! ZLD OS CLI Format with Mini-ISDB\n';
  script += '! ============================================\n\n';

  // ============================================
  // BLOCK 0: Factory Default Cleanup (HAL Dynamic)
  // ============================================
  // Zyxel factory devices group ports into default bridges (lan1, lan2).
  // We must remove ports from these bridges before using them for LAG or VLAN.
  // HAL integration ensures we use correct port names per model.
  
  const hardwareModel = getModelByName(input.modello);
  const vendorDef = getVendorForModel(input.modello);
  const isUOS = vendorDef?.cliType === 'zyxel_uos';
  const maxInterfaceNameLength = isUOS ? 11 : 32; // uOS enforces strict 11-char limit
  
  script += '! ============================================\n';
  script += '! BLOCK 0: Factory Default Cleanup (HAL Dynamic)\n';
  script += '! ============================================\n';
  script += `! Model: ${hardwareModel?.displayName || input.modello}\n`;
  script += `! CLI Type: ${vendorDef?.cliType || 'zyxel_zld'}\n`;
  script += `! Interface Name Limit: ${maxInterfaceNameLength} characters\n`;
  script += '! Remove selected ports from default bridges to prevent "Port in use" errors\n\n';
  
  // Collect all ports that need to be removed from default bridges
  const portsToClean: string[] = [];
  
  // Add LACP member ports
  if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
    input.lacp_aggregates.forEach((lacp) => {
      lacp.member_ports.forEach((port) => {
        if (!portsToClean.includes(port)) {
          portsToClean.push(port);
        }
      });
    });
  }
  
  // Add WAN ports if they're physical interfaces
  if (input.wan1?.enabled && input.wan1.physical_port && !input.wan1.is_vlan) {
    if (!portsToClean.includes(input.wan1.physical_port)) {
      portsToClean.push(input.wan1.physical_port);
    }
  }
  if (input.wan2?.enabled && input.wan2.physical_port && !input.wan2.is_vlan) {
    if (!portsToClean.includes(input.wan2.physical_port)) {
      portsToClean.push(input.wan2.physical_port);
    }
  }
  
  // Add management port if configured
  if (input.mgmt_port) {
    if (!portsToClean.includes(input.mgmt_port)) {
      portsToClean.push(input.mgmt_port);
    }
  }
  
  if (portsToClean.length > 0) {
    // Remove from lan1 (default bridge for trusted ports)
    script += 'interface lan1\n';
    portsToClean.forEach((port) => {
      script += `  no member ${port}\n`;
    });
    script += 'exit\n\n';
    
    // Remove from lan2 (default bridge for DMZ/optional ports)
    script += 'interface lan2\n';
    portsToClean.forEach((port) => {
      script += `  no member ${port}\n`;
    });
    script += 'exit\n\n';
    
    // Also disable default DHCP servers that might conflict
    script += '! Disable default DHCP servers to prevent conflicts\n';
    script += 'no ip dhcp pool LAN1_POOL\n';
    script += 'no ip dhcp pool LAN2_POOL\n\n';
  } else {
    script += '! No ports require cleanup from default bridges\n\n';
  }

  // ============================================
  // BLOCK 1: Initial Setup
  // ============================================
  script += '! ============================================\n';
  script += '! BLOCK 1: Initial Setup\n';
  script += '! ============================================\n\n';
  script += 'configure terminal\n';
  script += `  hostname ${input.hostname}\n`;
  script += `  clock timezone ${ZYXEL_TIMEZONE_MAP[input.timezone_id] || 'GMT+1'}\n`;
  script += `  username admin password ${input.password_admin}\n`;
  if (input.dns_primary) {
    script += `  ip name-server ${input.dns_primary}${input.dns_secondary ? ' ' + input.dns_secondary : ''}\n`;
  }
  script += 'exit\n\n';

  // ============================================
  // BLOCK 2: WAN Interfaces
  // ============================================
  if (input.wan1?.enabled || input.wan2?.enabled) {
    script += '! ============================================\n';
    script += '! BLOCK 2: WAN Interface Configuration\n';
    script += '! ============================================\n\n';
    if (input.wan1?.enabled) script += generateWANInterface(input.wan1, 'WAN1', 1);
    if (input.wan2?.enabled) script += generateWANInterface(input.wan2, 'WAN2', 2);
  }

  // ============================================
  // BLOCK 3: LACP (LAG) Configuration
  // ============================================
  if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
    script += '! ============================================\n';
    script += '! BLOCK 3: LACP Trunk (LAG) Configuration\n';
    script += '! ============================================\n\n';
    input.lacp_aggregates.forEach((lacp, index) => {
      const lagName = `lag${index + 1}`;
      script += `interface ${lagName}\n`;
      script += '  zone SYSTEM\n';
      lacp.member_ports.forEach(port => script += `  member ${port}\n`);
      
      // Native Management Network on LACP Parent Interface (only if this LAG is selected)
      if (input.managementNetwork?.enabled && input.managementNetwork.interface === lacp.name) {
        script += '  ! Native Management Network (Untagged)\n';
        script += `  ip address ${input.managementNetwork.ipAddress} ${input.managementNetwork.subnetMask}\n`;
        script += '  management enable\n';
      } else if (lacp.has_native_ip && lacp.native_ip && lacp.native_subnet) {
        script += `  ip address ${lacp.native_ip} ${lacp.native_subnet}\n`;
      }
      script += 'exit\n\n';
    });
    
    // DHCP Server for Native Management Network on LAG parent
    if (input.managementNetwork?.enabled && input.managementNetwork.dhcpEnabled &&
        input.lacp_aggregates.some(l => l.name === input.managementNetwork?.interface)) {
      const mgmtIp = input.managementNetwork.ipAddress;
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      const poolSize = calculatePoolSize(dhcpStart, dhcpEnd);
      const lagIndex = input.lacp_aggregates.findIndex(l => l.name === input.managementNetwork?.interface);
      
      script += '! DHCP Server for Native Management Network\n';
      script += 'ip dhcp pool NATIVE_MGMT_POOL\n';
      script += `  interface lag${lagIndex + 1}\n`;
      script += `  starting-address ${dhcpStart}\n`;
      script += `  pool-size ${poolSize}\n`;
      script += `  default-router ${mgmtIp}\n`;
      if (input.dns_primary) script += `  dns-server1 ${input.dns_primary}\n`;
      script += 'exit\n\n';
    }
  }
  
  // BLOCK 3b: Native Management Network on Standalone Physical Port
  if (input.managementNetwork?.enabled && input.managementNetwork.interface &&
      !input.lacp_aggregates?.some(l => l.name === input.managementNetwork?.interface)) {
    const portName = input.managementNetwork.interface;
    const mgmtIp = input.managementNetwork.ipAddress;
    const mgmtSubnet = input.managementNetwork.subnetMask;
    
    script += '! ============================================\n';
    script += '! BLOCK 3b: Native Management Network (Standalone Port)\n';
    script += '! ============================================\n\n';
    
    script += `interface ${portName}\n`;
    script += '  zone SYSTEM\n';
    script += `  ip address ${mgmtIp} ${mgmtSubnet}\n`;
    script += '  management enable\n';
    script += '  description "Native Management Network"\n';
    script += 'exit\n\n';
    
    // DHCP Server for Native Management Network on Standalone Port
    if (input.managementNetwork.dhcpEnabled) {
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      const poolSize = calculatePoolSize(dhcpStart, dhcpEnd);
      
      script += '! DHCP Server for Native Management Network (Standalone Port)\n';
      script += 'ip dhcp pool NATIVE_MGMT_POOL\n';
      script += `  interface ${portName}\n`;
      script += `  starting-address ${dhcpStart}\n`;
      script += `  pool-size ${poolSize}\n`;
      script += `  default-router ${mgmtIp}\n`;
      if (input.dns_primary) script += `  dns-server1 ${input.dns_primary}\n`;
      script += 'exit\n\n';
    }
  }

  // ============================================
  // BLOCK 4: VLANs & LAN Interfaces (HAL Dynamic)
  // ============================================
  if (input.vlans && input.vlans.length > 0) {
    // Get available LAN ports from HAL for base interface fallback
    const halLanPorts = hardwareModel ? getLanPorts(input.modello) : [];
    const defaultBaseInterface = halLanPorts[0] || hardwareModel?.defaultLanPort || 'ge1';
    
    script += '! ============================================\n';
    script += '! BLOCK 4: VLAN & LAN Interface Configuration (HAL Dynamic)\n';
    script += '! ============================================\n';
    script += `! Available LAN Ports: ${halLanPorts.join(', ') || 'Unknown - using fallback'}\n`;
    if (isUOS) {
      script += '! WARNING: uOS enforces 11-character interface name limit!\n';
    }
    script += '\n';
    
    input.vlans.forEach((vlan) => {
      const zone = getZyxelZone(vlan.vlan_type);
      let baseInterface = vlan.porte_assegnate?.[0] || defaultBaseInterface;
      
      // Handle LACP: if assigned port is a LAG name, resolve to lagX
      if (input.lacp_aggregates) {
        const lagIndex = input.lacp_aggregates.findIndex(l => l.name === baseInterface);
        if (lagIndex >= 0) baseInterface = `lag${lagIndex + 1}`;
      }
      
      // Validate that the base interface is valid for this hardware model
      if (hardwareModel && !halLanPorts.includes(baseInterface) && !baseInterface.startsWith('lag')) {
        script += `! WARNING: Port "${baseInterface}" not found in HAL for ${hardwareModel.displayName}. Using anyway.\n`;
      }
      
      // SECURITY-FIRST: Determine access level based on VLAN type
      const securityTier = getVlanSecurityTierZyxel(vlan.vlan_type, vlan.nome_vlan);
      const managementLine = getZyxelManagementAccess(securityTier, false);
      
      // Apply interface name truncation for uOS models
      const vlanInterfaceName = isUOS 
        ? truncateInterfaceName(`vlan${vlan.vlan_id}`, maxInterfaceNameLength)
        : `vlan${vlan.vlan_id}`;
      
      script += `! VLAN: ${vlan.nome_vlan} [Security Tier: ${securityTier.toUpperCase()}]\n`;
      script += `! Base Interface: ${baseInterface} (from HAL or user selection)\n`;
      script += `vlan ${vlan.vlan_id}\n  name "${isUOS ? truncateInterfaceName(vlan.nome_vlan, 10) : vlan.nome_vlan}"\nexit\n\n`;
      script += `interface ${vlanInterfaceName}\n`;
      script += `  base-interface ${baseInterface}\n`;
      script += `  ip address ${vlan.gateway_ip} ${vlan.subnet_mask}\n`;
      // Only add management access if trusted or management tier
      if (managementLine) {
        script += managementLine;
      } else {
        script += `  ! SECURITY: No management access for untrusted network\n`;
      }
      script += `  zone ${zone}\nexit\n\n`;
    });
  }

  // ============================================
  // BLOCK 5: Address Objects & DHCP
  // ============================================
  script += '! ============================================\n';
  script += '! BLOCK 5: Address Objects & DHCP Servers\n';
  script += '! ============================================\n\n';
  
  if (input.vlans && input.vlans.length > 0) {
    script += '! --- Network Address Objects ---\n';
    input.vlans.forEach((vlan) => {
      const objName = sanitizeObjectName(vlan.nome_vlan);
      script += `address-object ${objName}_SUBNET ${vlan.network_ip} ${vlan.subnet_mask}\n`;
    });
    script += '\n';
  }

  // Windows AD Service Objects (Fortinet Parity)
  // Generate these if AD policy is configured
  if (input.policies?.includes('lan_to_server') && 
      input.policy_config?.ad_source_vlan && 
      input.policy_config?.ad_dest_vlan) {
    script += '! --- Active Directory Service Objects (Fortinet Parity) ---\n';
    script += 'service-object TCP SRV_TCP_DCE_RPC 135\n';
    script += 'service-object TCP SRV_TCP_LDAP 389\n';
    script += 'service-object UDP SRV_UDP_LDAP 389\n';
    script += 'service-object TCP SRV_TCP_KERB 88\n';
    script += 'service-object UDP SRV_UDP_KERB 88\n';
    script += 'service-object TCP SRV_TCP_SMB 445\n';
    script += 'service-object TCP SRV_TCP_NETBIOS 139\n';
    script += 'service-object UDP SRV_UDP_NETBIOS_NS 137\n';
    script += 'service-object UDP SRV_UDP_NETBIOS_DGM 138\n\n';
    
    script += 'object-group service GRP_SRV_WINDOWS_AD\n';
    script += '  member SRV_TCP_DCE_RPC\n';
    script += '  member SRV_TCP_LDAP\n';
    script += '  member SRV_UDP_LDAP\n';
    script += '  member SRV_TCP_KERB\n';
    script += '  member SRV_UDP_KERB\n';
    script += '  member SRV_TCP_SMB\n';
    script += '  member SRV_TCP_NETBIOS\n';
    script += '  member SRV_UDP_NETBIOS_NS\n';
    script += '  member SRV_UDP_NETBIOS_DGM\n';
    script += '  member DNS\n';
    script += 'exit\n\n';
  }

  // DHCP Servers
  const dhcpVlans = input.vlans?.filter(v => v.enable_dhcp) || [];
  if (dhcpVlans.length > 0) {
    script += '! --- DHCP Server Pools ---\n';
    dhcpVlans.forEach((vlan) => {
      const poolName = sanitizeObjectName(vlan.nome_vlan) + '_POOL';
      const networkParts = vlan.network_ip.split('.').slice(0, 3).join('.');
      const startIp = vlan.dhcp_start || `${networkParts}.100`;
      const endIp = vlan.dhcp_end || `${networkParts}.250`;
      const poolSize = calculatePoolSize(startIp, endIp);
      script += `ip dhcp pool ${poolName}\n`;
      script += `  network ${vlan.network_ip} ${vlan.subnet_mask}\n`;
      script += `  default-router ${vlan.gateway_ip}\n`;
      script += `  starting-address ${startIp} pool-size ${poolSize}\n`;
      script += `  domain-name-server ${input.dns_primary || '8.8.8.8'} ${input.dns_secondary || '8.8.4.4'}\n`;
      script += '  lease 86400\nexit\n\n';
    });
  }

  // ============================================
  // BLOCK 5.5: FQDN Objects & Groups (Mini-ISDB)
  // ============================================
  // Get services that have at least one VLAN assigned
  const servicesWithVlans = Object.entries(input.cloud_services_mapping || {})
    .filter(([_, vlanIds]) => vlanIds && vlanIds.length > 0)
    .map(([serviceKey]) => serviceKey);

  if (servicesWithVlans.length > 0) {
    script += '! ============================================\n';
    script += '! BLOCK 5.5: App FQDN Objects (Mini-ISDB)\n';
    script += '! ============================================\n\n';
    
    servicesWithVlans.forEach(serviceKey => {
      const service = CLOUD_SERVICES_FQDN[serviceKey];
      if (!service) return;
      
      script += `! --- ${service.displayName} ---\n`;
      
      // Create individual FQDN objects
      service.fqdns.forEach((fqdn, idx) => {
        script += `address-object fqdn OBJ_FQDN_${service.name}_${idx + 1} ${fqdn}\n`;
      });
      
      // Create address group
      script += `object-group address GRP_APP_${service.name}\n`;
      service.fqdns.forEach((_, idx) => {
        script += `  member OBJ_FQDN_${service.name}_${idx + 1}\n`;
      });
      script += 'exit\n\n';
    });
  }

  // ============================================
  // BLOCK 6: UTM Content Filter Profiles
  // ============================================
  script += '! ============================================\n';
  script += '! BLOCK 6: UTM Content Filter Profiles\n';
  script += '! ============================================\n\n';
  
  // Corporate Profile
  script += 'profile content-filter CF_CORPORATE\n';
  script += '  description "Content filter for corporate networks"\n';
  if (input.webfilter_config?.block_adult) script += '  category-action 1 block    ! Adult/Mature Content\n';
  if (input.webfilter_config?.block_malicious) script += '  category-action 43 block   ! Malware\n  category-action 18 block   ! Phishing\n';
  if (input.webfilter_config?.block_gambling) script += '  category-action 14 block   ! Gambling\n';
  if (input.webfilter_config?.block_proxy) script += '  category-action 40 block   ! Proxy Avoidance\n';
  if (input.webfilter_config?.block_social) script += '  category-action 36 block   ! Social Networking\n';
  script += '  action forward\n  log enable\nexit\n\n';
  
  // Guest Profile (more restrictive)
  script += 'profile content-filter CF_GUEST\n';
  script += '  description "Content filter for guest networks - restrictive"\n';
  script += '  category-action 1 block    ! Adult/Mature Content\n';
  script += '  category-action 43 block   ! Malware\n';
  script += '  category-action 18 block   ! Phishing\n';
  script += '  category-action 14 block   ! Gambling\n';
  script += '  category-action 40 block   ! Proxy Avoidance\n';
  script += '  category-action 36 block   ! Social Networking\n';
  script += '  category-action 74 block   ! Peer-to-Peer\n';
  script += '  action forward\n  log enable\nexit\n\n';

  // ============================================
  // BLOCK 7: Firewall Policies (Secure Policies)
  // ============================================
  script += '! ============================================\n';
  script += '! BLOCK 7: Firewall Secure Policies\n';
  script += '! ============================================\n\n';
  
  let policyId = 10;
  
  // 7.1: App-Specific Policies for EACH VLAN->Service mapping
  if (servicesWithVlans.length > 0 && input.vlans && input.vlans.length > 0) {
    script += '! --- App-Specific Policies (VLAN -> App Mapping) ---\n';
    
    // Iterate through each service that has VLANs assigned
    Object.entries(input.cloud_services_mapping || {}).forEach(([serviceKey, vlanIds]) => {
      if (!vlanIds || vlanIds.length === 0) return;
      
      const service = CLOUD_SERVICES_FQDN[serviceKey];
      if (!service) return;
      
      // For each VLAN assigned to this service, create a policy
      vlanIds.forEach(vlanId => {
        const vlan = input.vlans?.find(v => v.vlan_id === vlanId);
        if (!vlan) return;
        
        const vlanName = sanitizeObjectName(vlan.nome_vlan);
        const zone = getZyxelZone(vlan.vlan_type);
        
        script += `secure-policy ${policyId}\n`;
        script += `  name "${vlanName}_TO_${service.name}"\n`;
        script += `  from ${zone}\n`;
        script += '  to WAN\n';
        script += `  source ${vlanName}_SUBNET\n`;
        script += `  destination GRP_APP_${service.name}\n`;
        script += '  service HTTPS HTTP\n';
        script += '  action allow\n';
        script += '  log session-start\n';
        script += 'exit\n\n';
        policyId += 1;
      });
    });
  }
  
  // 7.2: Corporate to WAN (Full Access with UTM)
  const corpVlans = input.vlans?.filter(v => v.vlan_type === 'corporate' || v.vlan_type === 'server') || [];
  const guestVlan = input.vlans?.find(v => v.vlan_type === 'guest');
  if (corpVlans.length > 0) {
    script += '! --- Corporate Internet Access ---\n';
    script += `secure-policy ${policyId}\n`;
    script += '  name "CORP_TO_INTERNET"\n';
    script += '  from LAN1\n';
    script += '  to WAN\n';
    script += '  source any\n';
    script += '  destination any\n';
    script += '  service HTTP HTTPS DNS';
    if (input.outbound_services?.imaps) script += ' IMAPS';
    if (input.outbound_services?.smtps) script += ' SMTPS';
    if (input.outbound_services?.ftp) script += ' FTP';
    if (input.outbound_services?.ntp) script += ' NTP';
    script += '\n';
    script += '  profile content-filter CF_CORPORATE\n';
    script += '  action allow\n';
    script += '  log session-start\n';
    script += 'exit\n\n';
    policyId += 1;
  }
  
  // 7.3: Guest to WAN (Limited + UTM)
  if (guestVlan) {
    script += '! --- Guest Internet Access (Limited) ---\n';
    script += `secure-policy ${policyId}\n`;
    script += '  name "GUEST_TO_INTERNET"\n';
    script += '  from LAN2\n';
    script += '  to WAN\n';
    script += `  source ${sanitizeObjectName(guestVlan.nome_vlan)}_SUBNET\n`;
    script += '  destination any\n';
    script += '  service HTTP HTTPS DNS\n';
    script += '  profile content-filter CF_GUEST\n';
    script += '  action allow\n';
    script += '  log session-start\n';
    script += 'exit\n\n';
    policyId += 1;
  }
  
  // 7.4: Block Guest to Corporate
  if (guestVlan && corpVlans.length > 0) {
    script += '! --- Block Guest to Corporate Networks ---\n';
    script += `secure-policy ${policyId}\n`;
    script += '  name "BLOCK_GUEST_TO_CORP"\n';
    script += '  from LAN2\n  to LAN1\n';
    script += '  source any\n  destination any\n';
    script += '  service any\n  action deny\n  log session-start\nexit\n\n';
    policyId += 10;
  }

  // 7.5: LAN to AD Server Policy (Windows Active Directory)
  if (input.policies?.includes('lan_to_server') && 
      input.policy_config?.ad_source_vlan && 
      input.policy_config?.ad_dest_vlan) {
    const sourceVlan = input.vlans?.find(v => v.nome_vlan === input.policy_config.ad_source_vlan);
    const destVlan = input.vlans?.find(v => v.nome_vlan === input.policy_config.ad_dest_vlan);
    
    if (sourceVlan && destVlan) {
      const sourceObjName = sanitizeObjectName(sourceVlan.nome_vlan);
      const destObjName = sanitizeObjectName(destVlan.nome_vlan);
      
      script += '! --- LAN to AD Server (Windows AD Services) ---\n';
      script += `secure-policy ${policyId}\n`;
      script += '  name "LAN_TO_AD_SERVER"\n';
      script += '  from LAN1\n  to LAN1\n';
      script += `  source ${sourceObjName}_SUBNET\n`;
      script += `  destination ${destObjName}_SUBNET\n`;
      script += '  service GRP_SRV_WINDOWS_AD\n';
      script += '  action allow\n';
      script += '  log session-start\n';
      script += 'exit\n\n';
      policyId += 1;
    }
  }

  // ============================================
  // BLOCK 7.8: APP REACHABILITY (L7/FQDN)
  // CRITICAL: These rules MUST be BEFORE general outbound policies!
  // FIX 2: Now supports dual-action (blocked_vlans + allowed_vlans)
  // Uses FQDN wildcards since Zyxel doesn't have Fortinet-style ISDB
  // ============================================
  if (input.app_reachability?.enabled && input.app_reachability.rules && input.app_reachability.rules.length > 0) {
    script += '! ============================================\n';
    script += '! BLOCK APP-L7: App & Social Reachability Rules\n';
    script += '! CRITICAL: These rules MUST be BEFORE general outbound!\n';
    script += '! ============================================\n\n';
    
    let appPolicyId = 7800; // Start App Policy IDs from 7800
    
    // First, create FQDN objects for each app
    script += '! --- App FQDN Objects ---\n';
    const createdFqdnObjects: Set<string> = new Set();
    
    input.app_reachability.rules.forEach((rule) => {
      const appDef = getAppDefinition(rule.appId);
      if (!appDef || !appDef.fqdns) return;
      
      const fqdnGroupName = `FQDN_APP_${appDef.id.toUpperCase()}`;
      if (createdFqdnObjects.has(fqdnGroupName)) return;
      createdFqdnObjects.add(fqdnGroupName);
      
      // Create FQDN objects for this app
      appDef.fqdns.forEach((fqdn, fIndex) => {
        const fqdnObjName = `${fqdnGroupName}_${fIndex}`;
        script += `address-object ${fqdnObjName} fqdn ${fqdn}\n`;
      });
      
      // Create address group containing all FQDNs
      script += `address-object-group ${fqdnGroupName}\n`;
      appDef.fqdns.forEach((_, fIndex) => {
        script += `  member ${fqdnGroupName}_${fIndex}\n`;
      });
      script += 'exit\n\n';
    });
    
    // Generate firewall policies - FIX 2: Dual-action support
    script += '! --- App Reachability Policies ---\n';
    input.app_reachability.rules.forEach((rule) => {
      const appDef = getAppDefinition(rule.appId);
      if (!appDef || !appDef.fqdns) return;
      
      const fqdnGroupName = `FQDN_APP_${appDef.id.toUpperCase()}`;
      
      // FIX 2: Process BLOCKED VLANs (deny policies)
      if (rule.blocked_vlans && rule.blocked_vlans.length > 0) {
        const blockedVlanObjs = input.vlans.filter((v: VLANInput) => rule.blocked_vlans.includes(v.nome_vlan));
        blockedVlanObjs.forEach((vlan: VLANInput) => {
          const vlanZone = getZyxelZone(vlan.vlan_type);
          script += `! [L7-BLOCK] ${appDef.name} from ${vlan.nome_vlan}\n`;
          script += `firewall rule ${appPolicyId}\n`;
          script += `  src-zone ${vlanZone}\n`;
          script += `  dst-zone WAN\n`;
          script += `  src-address OBJ_${vlan.nome_vlan}\n`;
          script += `  dst-address ${fqdnGroupName}\n`;
          script += `  service any\n`;
          script += `  action deny\n`;
          script += `  log session-start\n`;
          script += `  description "App Reachability: BLOCK ${appDef.name} for ${vlan.nome_vlan}"\n`;
          script += 'exit\n\n';
          appPolicyId += 1;
        });
      }
      
      // FIX 2: Process ALLOWED VLANs (permit policies - explicit allow)
      if (rule.allowed_vlans && rule.allowed_vlans.length > 0) {
        const allowedVlanObjs = input.vlans.filter((v: VLANInput) => rule.allowed_vlans.includes(v.nome_vlan));
        allowedVlanObjs.forEach((vlan: VLANInput) => {
          const vlanZone = getZyxelZone(vlan.vlan_type);
          script += `! [L7-ALLOW] ${appDef.name} from ${vlan.nome_vlan}\n`;
          script += `firewall rule ${appPolicyId}\n`;
          script += `  src-zone ${vlanZone}\n`;
          script += `  dst-zone WAN\n`;
          script += `  src-address OBJ_${vlan.nome_vlan}\n`;
          script += `  dst-address ${fqdnGroupName}\n`;
          script += `  service any\n`;
          script += `  action permit\n`;
          script += `  log session-start\n`;
          script += `  description "App Reachability: ALLOW ${appDef.name} for ${vlan.nome_vlan}"\n`;
          script += 'exit\n\n';
          appPolicyId += 1;
        });
      }
    });
  }

  // ============================================
  // BLOCK 7.9: SMART POLICIES
  // CRITICAL: DROP rules (Priority 1) first, then ALLOW rules (Priority 2)
  // ============================================
  if (input.smart_policies && input.vlans && input.vlans.length > 0) {
    const sp = input.smart_policies;
    
    // Check if any policy is enabled
    const hasAnyPolicy = 
      sp.policy_block_high_risk ||
      sp.policy_isolate_guest ||
      sp.policy_admin_omnipresence ||
      sp.policy_voip_outbound ||
      sp.policy_allow_printers ||
      sp.policy_allow_casting ||
      sp.policy_vendor_ot;
      
    if (hasAnyPolicy) {
      script += '! ============================================\n';
      script += '! BLOCK SMART: Smart Policy Builder Rules\n';
      script += '! ============================================\n\n';
      
      // Find VLANs by keywords
      const adminVlans = findVlansByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.admin);
      const staffVlans = findVlansByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.staff);
      const guestVlans = findVlansByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.guest);
      const printerVlan = findVlanByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.printer);
      const voipVlans = findVlansByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.voip);
      const iotVlans = findVlansByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.iot);
      const otVlan = findVlanByKeywordsZyxel(input.vlans, VLAN_KEYWORDS.ot);
      
      // DEBUG: Log resolved VLANs
      console.log('[SmartPolicies-Zyxel] Resolved VLANs:', {
        admin: adminVlans.map(v => v.nome_vlan),
        staff: staffVlans.map(v => v.nome_vlan),
        guest: guestVlans.map(v => v.nome_vlan),
        printer: printerVlan?.nome_vlan,
        voip: voipVlans.map(v => v.nome_vlan),
        iot: iotVlans.map(v => v.nome_vlan),
        ot: otVlan?.nome_vlan,
      });
      
      let smartPolicyId = 9000; // Start Smart Policy IDs from 9000
      
      // --- SERVICE OBJECTS ---
      script += '! --- Smart Policy Service Objects ---\n';
      
      if (sp.policy_allow_printers) {
        script += 'service-object SVC_PRINTERS\n';
        script += '  tcp 9100 9100\n';
        script += '  tcp 631 631\n';
        script += '  tcp 515 515\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_allow_casting) {
        script += 'service-object SVC_CASTING_TCP\n';
        script += '  tcp 7000 7100\n';
        script += '  tcp 8008 8009\n';
        script += 'exit\n';
        script += 'service-object SVC_CASTING_UDP\n';
        script += '  udp 5353 5353\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_voip_outbound) {
        script += 'service-object SVC_VOIP_SIP\n';
        script += '  udp 5060 5061\n';
        script += 'exit\n';
        script += 'service-object SVC_VOIP_RTP\n';
        script += '  udp 10000 64999\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_block_high_risk) {
        script += 'service-object SVC_HIGH_RISK\n';
        script += '  tcp 445 445\n';
        script += '  udp 445 445\n';
        script += '  tcp 3389 3389\n';
        script += '  tcp 1433 1433\n';
        script += '  tcp 3306 3306\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_vendor_ot) {
        script += 'service-object SVC_OT_HVAC\n';
        script += '  tcp 502 502\n';
        script += '  udp 47808 47808\n';
        script += 'exit\n\n';
      }
      
      // RFC1918 for Guest Isolation
      if (sp.policy_isolate_guest && guestVlans.length > 0) {
        script += '! RFC1918 Private Networks\n';
        script += 'address-object RFC1918_10 10.0.0.0/8\nexit\n';
        script += 'address-object RFC1918_172 172.16.0.0/12\nexit\n';
        script += 'address-object RFC1918_192 192.168.0.0/16\nexit\n';
        script += 'address-group GRP_RFC1918\n';
        script += '  member RFC1918_10 RFC1918_172 RFC1918_192\n';
        script += 'exit\n\n';
      }
      
      // --- PRIORITY 1: DROP RULES ---
      script += '! --- [P1-DROP] Smart Policy DROP Rules ---\n';
      
      // 1a. Block High-Risk to WAN
      if (sp.policy_block_high_risk) {
        script += `secure-policy ${smartPolicyId}\n`;
        script += '  name "SP_BLOCK_HIGH_RISK_OUT"\n';
        script += '  from LAN1\n  to WAN\n';
        script += '  source any\n  destination any\n';
        script += '  service SVC_HIGH_RISK\n';
        script += '  action deny\n';
        script += '  log session-start\n';
        script += 'exit\n\n';
        smartPolicyId += 1;
      }
      
      // 1b. Isolate Guest
      if (sp.policy_isolate_guest && guestVlans.length > 0) {
        guestVlans.forEach(guestVlan => {
          const guestObjName = sanitizeObjectName(guestVlan.nome_vlan);
          
          script += `secure-policy ${smartPolicyId}\n`;
          script += `  name "SP_ISOLATE_${guestObjName}"\n`;
          script += '  from LAN2\n  to LAN1\n';
          script += `  source ${guestObjName}_SUBNET\n`;
          script += '  destination GRP_RFC1918\n';
          script += '  service any\n';
          script += '  action deny\n';
          script += '  log session-start\n';
          script += 'exit\n\n';
          smartPolicyId += 1;
        });
      }
      
      // --- PRIORITY 2: ALLOW RULES ---
      script += '! --- [P2-ALLOW] Smart Policy ALLOW Rules ---\n';
      
      // 2a. Admin Omnipresence
      if (sp.policy_admin_omnipresence && adminVlans.length > 0) {
        adminVlans.forEach(adminVlan => {
          const adminObjName = sanitizeObjectName(adminVlan.nome_vlan);
          
          script += `secure-policy ${smartPolicyId}\n`;
          script += `  name "SP_ADMIN_${adminObjName}_OMNI"\n`;
          script += '  from LAN1\n  to any\n';
          script += `  source ${adminObjName}_SUBNET\n`;
          script += '  destination any\n';
          script += '  service any\n';
          script += '  action allow\n';
          script += '  log session-start\n';
          script += 'exit\n\n';
          smartPolicyId += 1;
        });
      }
      
      // 2b. VoIP Outbound
      if (sp.policy_voip_outbound && voipVlans.length > 0) {
        voipVlans.forEach(voipVlan => {
          const voipObjName = sanitizeObjectName(voipVlan.nome_vlan);
          
          script += `secure-policy ${smartPolicyId}\n`;
          script += `  name "SP_VOIP_${voipObjName}_OUT"\n`;
          script += '  from LAN1\n  to WAN\n';
          script += `  source ${voipObjName}_SUBNET\n`;
          script += '  destination any\n';
          script += '  service SVC_VOIP_SIP SVC_VOIP_RTP\n';
          script += '  action allow\n';
          script += '  nat enable\n';
          script += '  log session-start\n';
          script += 'exit\n\n';
          smartPolicyId += 1;
        });
      }
      
      // 2c. Allow Printers
      if (sp.policy_allow_printers && staffVlans.length > 0 && printerVlan) {
        const printerObjName = sanitizeObjectName(printerVlan.nome_vlan);
        
        staffVlans.forEach(staffVlan => {
          const staffObjName = sanitizeObjectName(staffVlan.nome_vlan);
          
          script += `secure-policy ${smartPolicyId}\n`;
          script += `  name "SP_${staffObjName}_TO_PRINT"\n`;
          script += '  from LAN1\n  to LAN1\n';
          script += `  source ${staffObjName}_SUBNET\n`;
          script += `  destination ${printerObjName}_SUBNET\n`;
          script += '  service SVC_PRINTERS\n';
          script += '  action allow\n';
          script += '  log session-start\n';
          script += 'exit\n\n';
          smartPolicyId += 1;
        });
      }
      
      // 2d. Allow Casting
      if (sp.policy_allow_casting && iotVlans.length > 0) {
        const castingSources = [...staffVlans, ...guestVlans];
        if (castingSources.length > 0) {
          iotVlans.forEach(iotVlan => {
            const iotObjName = sanitizeObjectName(iotVlan.nome_vlan);
            
            castingSources.forEach(srcVlan => {
              const srcObjName = sanitizeObjectName(srcVlan.nome_vlan);
              const srcZone = guestVlans.includes(srcVlan) ? 'LAN2' : 'LAN1';
              
              script += `secure-policy ${smartPolicyId}\n`;
              script += `  name "SP_${srcObjName}_CAST_${iotObjName}"\n`;
              script += `  from ${srcZone}\n  to LAN1\n`;
              script += `  source ${srcObjName}_SUBNET\n`;
              script += `  destination ${iotObjName}_SUBNET\n`;
              script += '  service SVC_CASTING_TCP SVC_CASTING_UDP\n';
              script += '  action allow\n';
              script += '  log session-start\n';
              script += 'exit\n\n';
              smartPolicyId += 1;
            });
          });
        }
      }
      
      // 2e. Admin/Management to OT (replaces Vendor to OT since we don't have Vendor preset)
      if (sp.policy_vendor_ot && otVlan) {
        const otObjName = sanitizeObjectName(otVlan.nome_vlan);
        
        // Use admin VLANs as source (Management network can access OT)
        if (adminVlans.length > 0) {
          adminVlans.forEach(adminVlan => {
            const adminObjName = sanitizeObjectName(adminVlan.nome_vlan);
            
            script += `secure-policy ${smartPolicyId}\n`;
            script += `  name "SP_${adminObjName}_TO_OT"\n`;
            script += '  from LAN1\n  to LAN1\n';
            script += `  source ${adminObjName}_SUBNET\n`;
            script += `  destination ${otObjName}_SUBNET\n`;
            script += '  service SVC_OT_HVAC\n';
            script += '  action allow\n';
            script += '  log session-start\n';
            script += 'exit\n\n';
            smartPolicyId += 1;
          });
        } else if (staffVlans.length > 0) {
          // Fallback: If no admin VLAN, allow Staff to OT
          staffVlans.forEach(staffVlan => {
            const staffObjName = sanitizeObjectName(staffVlan.nome_vlan);
            
            script += `secure-policy ${smartPolicyId}\n`;
            script += `  name "SP_${staffObjName}_TO_OT"\n`;
            script += '  from LAN1\n  to LAN1\n';
            script += `  source ${staffObjName}_SUBNET\n`;
            script += `  destination ${otObjName}_SUBNET\n`;
            script += '  service SVC_OT_HVAC\n';
            script += '  action allow\n';
            script += '  log session-start\n';
            script += 'exit\n\n';
            smartPolicyId += 1;
          });
        }
      }
    }
  }

  // ============================================
  // BLOCK 8: Management Interface
  // ============================================
  if (input.mgmt_port && input.mgmt_ip && input.mgmt_subnet) {
    script += '! ============================================\n';
    script += '! BLOCK 8: Management Interface\n';
    script += '! ============================================\n\n';
    script += `interface ${input.mgmt_port}\n`;
    script += `  ip address ${input.mgmt_ip} ${input.mgmt_subnet}\n`;
    script += '  zone SYSTEM\n  management https\n  management ssh\n  management ping\nexit\n\n';
  }

  // ============================================
  // BLOCK IOT: Zero-Trust IoT Configuration
  // ============================================
  script += generateZyxelIoTBlock(input.vlans, input.outbound_services);

  // ============================================
  // FOOTER
  // ============================================
  script += '! ============================================\n';
  script += '! Zyxel Script Generation Complete\n';
  script += `! VLANs: ${input.vlans?.length || 0} | LAGs: ${input.lacp_aggregates?.length || 0}\n`;
  script += `! App Policies: ${servicesWithVlans.length > 0 ? servicesWithVlans.map(k => CLOUD_SERVICES_FQDN[k]?.displayName).join(', ') : 'None'}\n`;
  script += '! ============================================\n\n';
  script += 'write memory\n! Configuration saved.\n';

  return script;
};

// ============================================
// IoT Zero-Trust Block for Zyxel ZLD
// ============================================
function generateZyxelIoTBlock(vlans: VLANInput[], outbound_services?: OutboundServicesConfig): string {
  // Check if IoT cloud services are enabled
  if (!outbound_services?.iot_cloud) {
    return '';
  }
  
  // Get IoT VLANs
  const iotVlans = vlans.filter(v => v.vlan_type === 'iot');
  if (iotVlans.length === 0) {
    return '';
  }
  
  let script = '';
  
  script += '! ============================================\n';
  script += '! BLOCK IOT: Zero-Trust IoT/Domotics Configuration\n';
  script += '! ============================================\n\n';
  
  // --- FQDN Address Objects ---
  script += '! --- IoT FQDN Address Objects ---\n';
  let fqdnIndex = 0;
  Object.entries(IOT_FQDN_GROUPS).forEach(([category, fqdns]) => {
    fqdns.forEach(fqdn => {
      fqdnIndex++;
      const objName = `IOT_FQDN_${String(fqdnIndex).padStart(3, '0')}`;
      script += `address-object fqdn ${objName} ${fqdn}\n`;
    });
  });
  script += '\n';
  
  // --- FQDN Address Group ---
  script += '! --- IoT FQDN Address Group ---\n';
  script += 'object-group address GRP_FQDN_IOT\n';
  for (let i = 1; i <= fqdnIndex; i++) {
    script += `  member IOT_FQDN_${String(i).padStart(3, '0')}\n`;
  }
  script += 'exit\n\n';
  
  // --- Service Objects (TCP) ---
  script += '! --- IoT Service Objects (TCP) ---\n';
  IOT_TCP_PORT_RANGES.forEach((portRange) => {
    const ports = portRange.split('-');
    const svcName = `IOT_TCP_${portRange.replace('-', '_')}`;
    if (ports[1]) {
      script += `service-object TCP ${svcName} ${ports[0]}-${ports[1]}\n`;
    } else {
      script += `service-object TCP ${svcName} ${ports[0]}\n`;
    }
  });
  script += '\n';
  
  // --- Service Objects (UDP) ---
  script += '! --- IoT Service Objects (UDP) ---\n';
  IOT_UDP_PORT_RANGES.forEach((portRange) => {
    const ports = portRange.split('-');
    const svcName = `IOT_UDP_${portRange.replace('-', '_')}`;
    if (ports[1]) {
      script += `service-object UDP ${svcName} ${ports[0]}-${ports[1]}\n`;
    } else {
      script += `service-object UDP ${svcName} ${ports[0]}\n`;
    }
  });
  script += '\n';
  
  // --- Service Group ---
  script += '! --- IoT Service Group ---\n';
  script += 'object-group service GRP_SRV_IOT\n';
  IOT_TCP_PORT_RANGES.forEach(p => script += `  member IOT_TCP_${p.replace('-', '_')}\n`);
  IOT_UDP_PORT_RANGES.forEach(p => script += `  member IOT_UDP_${p.replace('-', '_')}\n`);
  script += 'exit\n\n';
  
  // --- IoT VLAN Subnet Address Objects ---
  script += '! --- IoT VLAN Subnet Address Objects ---\n';
  iotVlans.forEach(vlan => {
    const objName = sanitizeObjectName(vlan.nome_vlan);
    script += `address-object ${objName}_IOT_SUBNET ${vlan.network_ip} ${vlan.subnet_mask}\n`;
  });
  script += '\n';
  
  // --- IoT Address Group (Source) ---
  script += '! --- IoT Source Address Group ---\n';
  script += 'object-group address GRP_NET_IOT\n';
  iotVlans.forEach(vlan => {
    const objName = sanitizeObjectName(vlan.nome_vlan);
    script += `  member ${objName}_IOT_SUBNET\n`;
  });
  script += 'exit\n\n';
  
  // --- Firewall Policy: IoT to Internet (Restricted) ---
  script += '! --- IoT Firewall Policy (Zero-Trust) ---\n';
  script += 'secure-policy 100\n';
  script += '  name "IOT_TO_INTERNET"\n';
  script += '  from LAN2\n';
  script += '  to WAN\n';
  script += '  source GRP_NET_IOT\n';
  script += '  destination GRP_FQDN_IOT\n';
  script += '  service GRP_SRV_IOT\n';
  script += '  action allow\n';
  script += '  log session-start\n';
  script += 'exit\n\n';
  
  // --- Block Policy: IoT to Internal Networks ---
  const nonIotVlans = vlans.filter(v => v.vlan_type !== 'iot');
  if (nonIotVlans.length > 0) {
    script += '! --- Block IoT to Internal Networks ---\n';
    script += 'secure-policy 101\n';
    script += '  name "BLOCK_IOT_TO_INTERNAL"\n';
    script += '  from LAN2\n';
    script += '  to LAN1\n';
    script += '  source GRP_NET_IOT\n';
    script += '  destination any\n';
    script += '  service any\n';
    script += '  action deny\n';
    script += '  log session-start\n';
    script += 'exit\n\n';
  }
  
  return script;
}

// ============================================
// WAN INTERFACE GENERATOR
// ============================================
function generateWANInterface(wan: WANConfig, name: string, index: number): string {
  let block = '';
  
  if (wan.is_vlan && wan.vlan_id) {
    block += `vlan ${wan.vlan_id}\n  name "${name}_VLAN"\nexit\n\n`;
    block += `interface vlan${wan.vlan_id}\n  base-interface ${wan.physical_port}\n  zone WAN\n`;
  } else {
    block += `interface ${wan.physical_port}\n  zone WAN\n`;
  }
  
  if (wan.type === 'dhcp') {
    block += '  ip address dhcp\n';
  } else if (wan.type === 'static' && wan.ip && wan.subnet) {
    block += `  ip address ${wan.ip} ${wan.subnet}\n`;
    if (wan.gateway) block += `  gateway ${wan.gateway}\n`;
  } else if (wan.type === 'pppoe') {
    block += '  encapsulation pppoe\n';
    if (wan.username) block += `  pppoe username ${wan.username}\n`;
    if (wan.password) block += `  pppoe password ${wan.password}\n`;
  }
  
  block += 'exit\n\n';
  
  if (index === 1 && wan.type === 'static' && wan.gateway) {
    block += `ip route 0.0.0.0 0.0.0.0 ${wan.gateway}\n\n`;
  }
  
  return block;
}

export default generateZyxelCLIScript;
